import * as React from 'react'
import { cn } from '@/lib/utils'

type TabsContextValue = {
  value: string
  setValue: (v: string) => void
  baseId: string
}

const TabsContext = React.createContext<TabsContextValue | null>(null)

function useTabsContext() {
  const ctx = React.useContext(TabsContext)
  if (!ctx) throw new Error('Tabs subcomponents must be inside <Tabs>')
  return ctx
}

export interface TabsProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
}

export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  ({ value, defaultValue, onChange, className, children, ...rest }, ref) => {
    const [internal, setInternal] = React.useState<string>(defaultValue ?? '')
    const current = value ?? internal
    const baseId = React.useId()
    const setValue = (v: string) => {
      if (value === undefined) setInternal(v)
      onChange?.(v)
    }
    return (
      <div ref={ref} className={cn('flex flex-col gap-2', className)} {...rest}>
        <TabsContext.Provider value={{ value: current, setValue, baseId }}>
          {children}
        </TabsContext.Provider>
      </div>
    )
  }
)
Tabs.displayName = 'Tabs'

/**
 * Move o foco entre as abas com as setas (padrão ARIA Tabs, ativação MANUAL).
 * Sem isso, o `tabIndex={-1}` das abas inativas as torna inalcançáveis pelo
 * teclado. A seta só move o foco; a ativação fica para Enter/Espaço (nativos do
 * `<button>`) — ativar a cada seta remontaria o painel e refaria as queries da
 * aba a cada tecla.
 */
function focusTab(tabs: HTMLButtonElement[], index: number) {
  const target = tabs[(index + tabs.length) % tabs.length]
  target?.focus()
}

export const TabsList = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, onKeyDown, ...rest }, ref) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented) return

    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End']
    if (!keys.includes(event.key)) return

    const tabs = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>(
        '[role="tab"]:not([disabled])'
      )
    )
    const current = tabs.indexOf(document.activeElement as HTMLButtonElement)
    if (current === -1) return

    event.preventDefault()
    if (event.key === 'ArrowRight') focusTab(tabs, current + 1)
    else if (event.key === 'ArrowLeft') focusTab(tabs, current - 1)
    else if (event.key === 'Home') focusTab(tabs, 0)
    else focusTab(tabs, tabs.length - 1)
  }

  return (
    <div
      ref={ref}
      role="tablist"
      onKeyDown={handleKeyDown}
      className={cn(
        'inline-flex items-center gap-1 border-b border-gray-200 dark:border-gray-700',
        className
      )}
      {...rest}
    />
  )
})
TabsList.displayName = 'TabsList'

export interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string
}

export const TabsTrigger = React.forwardRef<
  HTMLButtonElement,
  TabsTriggerProps
>(({ className, value, disabled, children, ...rest }, ref) => {
  const ctx = useTabsContext()
  const active = ctx.value === value
  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      id={`${ctx.baseId}-trigger-${value}`}
      aria-controls={`${ctx.baseId}-panel-${value}`}
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      disabled={disabled}
      onClick={() => ctx.setValue(value)}
      className={cn(
        // `inline-flex items-center gap-1.5`: o preflight do Tailwind põe
        // `svg { display: block }`, então um ícone dentro de um botão sem flex
        // quebra linha e cai POR CIMA do rótulo. Era o efeito visível em todas
        // as abas do app.
        'inline-flex items-center gap-1.5 -mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        active
          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200',
        className
      )}
      {...rest}
    >
      {children}
    </button>
  )
})
TabsTrigger.displayName = 'TabsTrigger'

export interface TabsPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string
}

export const TabsPanel = React.forwardRef<HTMLDivElement, TabsPanelProps>(
  ({ className, value, children, ...rest }, ref) => {
    const ctx = useTabsContext()
    const active = ctx.value === value
    return (
      <div
        ref={ref}
        role="tabpanel"
        id={`${ctx.baseId}-panel-${value}`}
        aria-labelledby={`${ctx.baseId}-trigger-${value}`}
        hidden={!active}
        className={cn('focus-visible:outline-none', className)}
        {...rest}
      >
        {active ? children : null}
      </div>
    )
  }
)
TabsPanel.displayName = 'TabsPanel'
